/* Verified colour photographs on collection cards with approved media maps. */
(() => {
  'use strict';
  const schema = 'corsaille.catalogue-metal-media-map.v1';
  const families = new Set(['white', 'yellow', 'rose']);
  const numericID = value => String(value || '').split('/').pop();
  const identity = value => {
    try { return new URL(value, 'https://corsaille.ro').pathname.split('/').pop(); }
    catch { return ''; }
  };
  const safePhoto = image => {
    if (image?.approved !== true || image.kind !== 'shopify_media' ||
        image.representation === 'generated' || image.generated || image.recolored || image.recoloured) return false;
    try {
      const url = new URL(image.url);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' &&
        url.pathname.startsWith('/s/files/1/1002/7116/1688/files/');
    } catch { return false; }
  };
  function resolvePhotos(manifest, handle, productID, variantID, family, excluded) {
    const product = manifest?.schema === schema && manifest.products?.[handle];
    if (!product || handle === 'epure-oval' || numericID(product.productId) !== numericID(productID) ||
        product.requiresGeometryReview || product.requiresComponentReview || product.requiresSampleSpecReview ||
        !families.has(family)) return [];
    const variant = product.variants?.[numericID(variantID)];
    if (variant?.family !== family || variant.material !== 'gold') return [];
    const seen = new Set();
    return (product.groups?.[family]?.images || []).filter(image => {
      if (!safePhoto(image) || image.family !== family ||
          [image.mediaId, image.sourceMediaId].some(id => id && excluded.has(numericID(id)))) return false;
      const key = identity(image.url);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  function orderedPhotos(images, role) {
    const preferred = images.find(image => role && image.role === role);
    return (preferred ? [preferred, ...images.filter(image => image !== preferred)] : images).slice(0, 3);
  }
  function sizedURL(source, width) {
    const url = new URL(source);
    url.searchParams.set('width', String(width));
    return url.href;
  }
  function variantURL(source, variantID, base) {
    if (!/^\d+$/.test(String(variantID))) throw new Error('Invalid variant');
    const url = new URL(source, base);
    url.searchParams.set('variant', variantID);
    return url.pathname + url.search + url.hash;
  }
  // Pure helpers are exported only to the local verification runner.
  if (typeof module === 'object' && module.exports) {
    module.exports = { resolvePhotos, orderedPhotos, sizedURL, variantURL, identity };
  }
  if (typeof document === 'undefined' || window.__corsailleCardMetalsV1) return;
  window.__corsailleCardMetalsV1 = true;
  const mounted = new WeakSet(), manifests = new Map(), decoded = new Map();
  let scanFrame = 0;
  function fetchManifest(source) {
    if (!manifests.has(source)) {
      const pending = fetch(source, { credentials: 'omit', signal: AbortSignal.timeout(10000) })
        .then(response => { if (!response.ok) throw new Error('Photo map unavailable'); return response.json(); })
        .catch(error => { manifests.delete(source); throw error; });
      manifests.set(source, pending);
    }
    return manifests.get(source);
  }
  function preparePhoto(source, sizes) {
    const src = sizedURL(source, 960);
    const srcset = [240, 360, 540, 720, 960].map(width => `${sizedURL(source, width)} ${width}w`).join(', ');
    const key = src + '|' + sizes;
    if (!decoded.has(key)) {
      const pending = new Promise((resolve, reject) => {
        const image = new Image();
        let done = false;
        const finish = error => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          image.onload = image.onerror = null;
          error ? reject(error) : resolve(image);
        };
        const timer = setTimeout(() => finish(new Error('Photo timed out')), 12000);
        image.decoding = 'async';
        image.onload = () => {
          if (image.decode) image.decode().then(() => finish(), () => finish(image.naturalWidth ? null : new Error('Photo decode failed')));
          else finish();
        };
        image.onerror = () => finish(new Error('Photo unavailable'));
        image.sizes = sizes;
        image.srcset = srcset;
        image.src = src;
      }).catch(error => { decoded.delete(key); throw error; });
      decoded.set(key, pending);
      if (decoded.size > 32) decoded.delete(decoded.keys().next().value);
    }
    return decoded.get(key);
  }
  function visibleRole(card, manifest, handle) {
    const track = card.querySelector('[data-cv4-track]');
    const slides = [...(track?.querySelectorAll('[data-cv4-slide]') || [])];
    const index = track?.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
    const image = slides[Math.max(0, Math.min(slides.length - 1, index))]?.querySelector('img');
    const key = identity(image?.currentSrc || image?.src);
    const all = Object.values(manifest.products?.[handle]?.groups || {}).flatMap(group => group.images || []);
    return all.find(frame => key && identity(frame.url) === key)?.role || '';
  }
  function replaceGallery(state, images, firstPhoto, label) {
    const oldTrack = state.card.querySelector('[data-cv4-track]');
    const gallery = oldTrack.closest('[data-cv4-gallery]');
    const oldImage = oldTrack.querySelector('img');
    const title = state.card.dataset.csProductTitle || state.handle;
    const track = oldTrack.cloneNode(false);
    images.forEach((photo, index) => {
      const slide = document.createElement('span');
      slide.setAttribute('data-cv4-slide', '');
      const image = index === 0 ? firstPhoto.cloneNode(false) : document.createElement('img');
      image.className = 'cs-product-card__image' + (index === 0 ? ' cs-product-card__image--primary' : '');
      image.alt = `${title} · ${label} · perspectiva ${index + 1}`;
      image.width = oldImage?.width || 800;
      image.height = oldImage?.height || 800;
      image.decoding = 'async';
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.sizes = firstPhoto.sizes;
      if (index > 0) {
        image.dataset.cv4Src = sizedURL(photo.url, 800);
        image.dataset.cv4Srcset = [320, 480, 800].map(width => `${sizedURL(photo.url, width)} ${width}w`).join(', ');
      }
      slide.append(image);
      track.append(slide);
    });
    const dots = document.createElement('div');
    dots.className = 'cv4-gallery__dots';
    dots.setAttribute('data-cv4-dots', '');
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', `Alege perspectiva pentru ${title}`);
    dots.hidden = images.length < 2;
    images.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.dataset.cv4Dot = String(index);
      dot.setAttribute('aria-label', `Imaginea ${index + 1} din ${images.length} · ${title}`);
      dot.setAttribute('aria-pressed', String(index === 0));
      const mark = document.createElement('span');
      mark.setAttribute('aria-hidden', 'true');
      dot.append(mark);
      dots.append(dot);
    });
    const oldDots = gallery.querySelector('[data-cv4-dots]');
    if (oldDots) oldDots.replaceWith(dots); else gallery.append(dots);
    // Replacing the track uses the existing gallery engine's explicit remount
    // path, which removes old listeners, stops its hint and preserves gestures.
    oldTrack.replaceWith(track);
    state.card.dataset.csMediaMode = 'packshot_contain';
  }
  function priceText(button) {
    return (button.dataset.metalFrom === 'true' ? 'De la ' : '') + button.dataset.metalPrice;
  }
  function commitChoice(state, button, images, photo, announce = true) {
    const label = button.dataset.metalLabel;
    const link = state.card.querySelector('[data-cs-product-link]');
    const href = variantURL(link.getAttribute('href'), button.dataset.variantId, location.href);
    replaceGallery(state, images, photo, label);
    link.setAttribute('href', href);
    state.card.dataset.csVariantId = button.dataset.variantId;
    state.group.dataset.currentFamily = button.dataset.csCardMetal;
    state.buttons.forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
    const currentPrice = state.card.querySelector('.cs-price__current');
    if (currentPrice) {
      currentPrice.replaceChildren();
      if (button.dataset.metalFrom === 'true') {
        const prefix = document.createElement('small');
        prefix.className = 'cs-v5-price-from';
        prefix.textContent = 'De la ';
        currentPrice.append(prefix);
      }
      currentPrice.append(document.createTextNode(button.dataset.metalPrice));
    }
    const summary = state.card.querySelector('[data-cs-card-metal-price-summary]');
    if (summary) summary.textContent = `${label}. ${priceText(button)}`;
    const save = state.card.querySelector('[data-corsaille-save]');
    if (save) {
      save.dataset.productUrl = href;
      save.dataset.productImage = sizedURL(images[0].url, 720);
      save.dataset.productPrice = priceText(button);
    }
    if (announce) state.status.textContent = `${label}. ${priceText(button)}. Fotografii și configurație actualizate.`;
  }
  // One brief left-to-right demonstration per collection/page visit.
  // Existing colour and gallery controls stop the complete sequence permanently.
  const mobileDemos = new Map(), cardStates = new WeakMap();
  const mobileDemoRoute = () => location.pathname + location.search;
  const demonstratedThisVisit = new Set();
  function mobileDemoSeen(collection) { return demonstratedThisVisit.has(collection); }
  function rememberMobileDemo(collection) { demonstratedThisVisit.add(collection); }
  function defaultDemoRoute(collection) {
    const url = new URL(location.href);
    if (!url.pathname.replace(/\/$/, '').endsWith('/collections/' + collection)) return false;
    return ![...url.searchParams].some(([key, value]) =>
      value && (key.startsWith('filter.') || key === 'variant' || (key === 'page' && value !== '1')));
  }
  function stopMobileDemo(record, phase) {
    clearTimeout(record.timer);
    cancelAnimationFrame(record.frame);
    record.signal.abort();
    record.observers.forEach(observer => observer.disconnect());
    record.phase = phase;
    record.entries.forEach(entry => {
      if (entry.state.card.isConnected) entry.state.card.dataset.csMetalDemo = phase;
    });
  }
  function cancelMobileDemoForChoice(state) {
    state.demoTouched = true;
    const collection = state.card.dataset.csCollection;
    const record = mobileDemos.get(collection);
    if (record) stopMobileDemo(record, 'customer-choice');
    rememberMobileDemo(collection);
  }
  function demoSnapshot(state) {
    const track = state.card.querySelector('[data-cv4-track]');
    const image = state.card.querySelector('[data-cv4-track] img');
    return JSON.stringify([state.group.dataset.currentFamily, state.card.dataset.csVariantId,
      state.card.querySelector('[data-cs-product-link]')?.getAttribute('href'),
      state.group.dataset.metalMap, state.group.dataset.productId, state.group.dataset.productHandle,
      identity(image?.currentSrc || image?.src), Math.round(track?.scrollLeft || 0),
      state.buttons.map(button => button.dataset.csCardMetal + ':' + button.dataset.variantId)]);
  }
  function demoEntryUnchanged(entry) {
    const state = entry.state;
    return state.card.isConnected && state.group.isConnected && !state.demoTouched &&
      state.revision === entry.revision && state.group.dataset.currentFamily === 'white' &&
      state.card.querySelector('[data-cv4-track]') === entry.track && demoSnapshot(state) === entry.snapshot;
  }
  function mobileDemoCurrent(record) {
    const row = [...record.grid.querySelectorAll('article[data-cs-product-card]')].slice(0, 2);
    return mobileDemoRoute() === record.route && row.length === 2 &&
      row.every((card, index) => card === record.row[index]) &&
      record.entries.every(entry => entry.state.group.isConnected && entry.state.card.isConnected &&
        !entry.state.demoTouched && entry.state.revision === entry.revision);
  }
  function mobileDemoRowVisible(record) {
    if (document.hidden || !window.matchMedia('(max-width: 749px)').matches ||
        record.row.some(card => card.closest('[inert], [aria-hidden="true"]'))) return false;
    const height = window.innerHeight, width = document.documentElement.clientWidth;
    let covered = Number.parseFloat(window.getComputedStyle(record.root).getPropertyValue('--cs-collection-sticky-offset')) || 0;
    const utility = record.root.querySelector('.cs-collection__utility-wrap--sticky');
    if (utility) {
      const box = utility.getBoundingClientRect();
      if (box.height > 0 && box.bottom > 0 && box.top < height) covered = Math.max(covered, box.bottom);
    }
    const boxes = record.media.map(node => node.getBoundingClientRect());
    return Math.abs(boxes[0].top - boxes[1].top) <= 4 && boxes.every(box =>
      box.width > 0 && box.height > 0 && box.left >= -1 && box.right <= width + 1 &&
      box.top >= covered - 1 && box.bottom <= height + 1) && record.media.every((node, index) => {
        const image = node.querySelector('img'), box = boxes[index];
        const hit = document.elementFromPoint(box.left + box.width / 2, box.top + box.height / 2);
        return image?.complete && image.naturalWidth > 0 && hit && node.contains(hit);
      });
  }
  async function prepareDemoEntry(entry) {
    const state = entry.state;
    const manifest = await fetchManifest(state.group.dataset.metalMap);
    if (!demoEntryUnchanged(entry)) throw new Error('Card changed');
    const role = visibleRole(state.card, manifest, state.handle);
    if (!role) throw new Error('No verified matching hero role');
    const activeImage = state.card.querySelector('[data-cv4-track] img');
    const sizes = activeImage?.sizes || '(min-width: 750px) 33vw, 50vw';
    const frames = await Promise.all(['white', 'yellow', 'rose'].map(async family => {
      const button = state.buttons.find(button => button.dataset.csCardMetal === family);
      const eligible = button && resolvePhotos(manifest, state.handle, state.group.dataset.productId,
        button.dataset.variantId, family, state.excluded);
      if (!eligible?.some(image => image.role === role)) throw new Error('Matching verified colour missing');
      const images = orderedPhotos(eligible, role);
      const photo = await preparePhoto(images[0].url, sizes);
      return { family, button, images, photo };
    }));
    if (!demoEntryUnchanged(entry)) throw new Error('Card changed');
    entry.frames = new Map(frames.map(frame => [frame.family, frame]));
  }
  async function preloadMobileDemo(record) {
    record.phase = 'loading';
    // All eligible cards are ready before playback. A missing right map never
    // delays the handoff or substitutes unverified images; it leaves that card still.
    const results = await Promise.allSettled(record.entries.map(prepareDemoEntry));
    if (record.phase !== 'loading') return;
    if (!mobileDemoCurrent(record) || results[0].status !== 'fulfilled') return stopMobileDemo(record, 'unavailable');
    record.entries = record.entries.filter((entry, index) => results[index].status === 'fulfilled');
    record.phase = 'ready';
    checkMobileDemo(record);
  }
  function startMobileDemo(record) {
    rememberMobileDemo(record.collection);
    record.signal.abort();
    record.observers.forEach(observer => observer.disconnect());
    record.phase = 'playing';
    const sequence = ['white', 'yellow', 'rose', 'white'];
    let cardIndex = 0, frameIndex = 0;
    const beginCard = () => {
      const entry = record.entries[cardIndex];
      if (!entry || !mobileDemoCurrent(record) || !demoEntryUnchanged(entry) || !mobileDemoRowVisible(record)) {
        return stopMobileDemo(record, cardIndex ? 'complete' : 'invalidated');
      }
      entry.state.card.dataset.csMetalDemo = 'playing';
      // Stops the existing gallery hint without counting as a customer action.
      entry.state.card.dispatchEvent(new CustomEvent('cs:gallery-interact', { detail: { source: 'metal-demo', automatic: true } }));
      advance();
    };
    const advance = () => {
      if (record.phase !== 'playing') return;
      if (!mobileDemoCurrent(record)) return stopMobileDemo(record, 'invalidated');
      const entry = record.entries[cardIndex], frame = entry.frames.get(sequence[frameIndex]);
      commitChoice(entry.state, frame.button, frame.images, frame.photo, false);
      if (++frameIndex < sequence.length) record.timer = setTimeout(advance, 800);
      else {
        entry.state.card.dataset.csMetalDemo = 'complete';
        frameIndex = 0;
        // The restored left white frame is also the right white starting frame:
        // left at 0/800/1600/2400 ms, right at 2400/3200/4000/4800 ms.
        if (++cardIndex < record.entries.length) beginCard();
        else stopMobileDemo(record, 'complete');
      }
    };
    beginCard();
  }
  function checkMobileDemo(record) {
    record.frame = 0;
    if (!['waiting', 'loading', 'ready'].includes(record.phase)) return;
    if (!mobileDemoCurrent(record) || mobileDemoSeen(record.collection) ||
        !record.entries.every(demoEntryUnchanged)) return stopMobileDemo(record, 'invalidated');
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const box = record.media[0].getBoundingClientRect();
    if (record.phase === 'waiting' && box.top < window.innerHeight + 300 && box.bottom > 0) preloadMobileDemo(record);
    if (record.phase === 'ready' && mobileDemoRowVisible(record)) startMobileDemo(record);
  }
  function scheduleMobileDemo(state) {
    const collection = state.card.dataset.csCollection;
    if (!window.matchMedia('(max-width: 749px)').matches || mobileDemos.has(collection) ||
        mobileDemoSeen(collection) || !defaultDemoRoute(collection) || state.demoTouched ||
        state.group.dataset.currentFamily !== 'white' || state.revision !== 0) return;
    const grid = state.card.closest('.cs-collection__grid'), root = state.card.closest('.cs-collection');
    const row = [...(grid?.querySelectorAll('article[data-cs-product-card]') || [])].slice(0, 2);
    const hasColours = state => state && !state.demoTouched && state.revision === 0 &&
      state.group.dataset.currentFamily === 'white' &&
      ['white', 'yellow', 'rose'].every(family => state.buttons.some(button => button.dataset.csCardMetal === family));
    if (!root || row.length !== 2 || row[0] !== state.card || !hasColours(state) ||
        row.some(card => cardStates.get(card)?.demoTouched)) return;
    const media = row.map(card => card.querySelector('.cs-product-card__media'));
    if (media.some(node => !node)) return;
    const entries = row.map(card => cardStates.get(card)).filter(hasColours).map(state => ({ state,
      revision: state.revision, track: state.card.querySelector('[data-cv4-track]'), snapshot: demoSnapshot(state) }));
    const record = { state, entries, collection, grid, root, row, media, route: mobileDemoRoute(),
      phase: 'waiting', signal: new AbortController(), observers: [], frame: 0, timer: 0 };
    mobileDemos.set(collection, record);
    record.request = () => { if (!record.frame) record.frame = requestAnimationFrame(() => checkMobileDemo(record)); };
    window.addEventListener('scroll', record.request, { passive: true, signal: record.signal.signal });
    window.addEventListener('resize', record.request, { passive: true, signal: record.signal.signal });
    document.addEventListener('visibilitychange', record.request, { signal: record.signal.signal });
    document.addEventListener('cs:overlay-change', record.request, { signal: record.signal.signal });
    root.addEventListener('toggle', record.request, { capture: true, signal: record.signal.signal });
    media.forEach(node => node.addEventListener('load', record.request, { capture: true, signal: record.signal.signal }));
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(record.request, { rootMargin: '300px 0px', threshold: [0, 1] });
      media.forEach(node => observer.observe(node)); record.observers.push(observer);
    }
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(record.request);
      [...media, ...document.querySelectorAll('.cs-announcement, .cs-header, .corsaille-header')].forEach(node => observer.observe(node));
      record.observers.push(observer);
    }
    record.request();
  }
  function watchDemoInteractions(state) {
    let gesture = null;
    const plain = event => !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
    const trackFor = event => event.target.closest?.('[data-cv4-track]');
    const listen = (type, callback) => state.card.addEventListener(type, callback, { capture: true, passive: true });
    // A pointer may start on the photograph simply because the shopper is
    // scrolling the page. Only a deliberate horizontal gallery gesture cancels.
    listen('pointerdown', event => {
      const track = trackFor(event);
      gesture = track && event.button === 0 && event.isPrimary !== false && plain(event) ?
        { id: event.pointerId, track, x: event.clientX, y: event.clientY, left: track.scrollLeft, at: Date.now(), active: true } : null;
    });
    listen('pointermove', event => {
      if (!gesture?.active || gesture.id !== event.pointerId) return;
      const dx = Math.abs(event.clientX - gesture.x), dy = Math.abs(event.clientY - gesture.y);
      if (dx > 10 && dx > dy * 1.15) cancelMobileDemoForChoice(state);
    });
    const endGesture = event => {
      if (gesture?.id === event.pointerId) { gesture.at = Date.now(); gesture.active = false; }
    };
    listen('pointerup', endGesture);
    listen('pointercancel', endGesture);
    // Native touch scrolling can start after pointercancel. A changed horizontal
    // track position confirms that gesture; a new automatic track cannot match.
    listen('scroll', event => {
      if (gesture && event.target === gesture.track && Date.now() - gesture.at < 1000 &&
          Math.abs(gesture.track.scrollLeft - gesture.left) > 8) cancelMobileDemoForChoice(state);
    });
    listen('wheel', event => {
      if (!trackFor(event)) return;
      const dx = Math.abs(event.deltaX || 0), dy = Math.abs(event.deltaY || 0);
      if ((dx > 0 && dx > dy * 1.15) || (event.shiftKey && dy > 0)) cancelMobileDemoForChoice(state);
    });
    listen('click', event => {
      if (event.target.closest?.('[data-cv4-dot]')) cancelMobileDemoForChoice(state);
    });
    listen('keydown', event => {
      if (trackFor(event) && plain(event) && ['ArrowLeft', 'ArrowRight'].includes(event.key)) cancelMobileDemoForChoice(state);
    });
    state.card.addEventListener('cs:gallery-interact', event => {
      if (!event.detail?.automatic) cancelMobileDemoForChoice(state);
    });
  }

  async function choose(state, button) {
    cancelMobileDemoForChoice(state);
    const revision = ++state.revision;
    const family = button.dataset.csCardMetal;
    state.card.dataset.cv4Interacted = 'true';
    state.card.dispatchEvent(new CustomEvent('cs:gallery-interact'));
    state.buttons.forEach(choice => choice.removeAttribute('aria-busy'));
    button.setAttribute('aria-busy', 'true');
    state.status.textContent = `Se încarcă fotografiile: ${button.dataset.metalLabel}.`;
    try {
      const manifest = await fetchManifest(state.group.dataset.metalMap);
      if (revision !== state.revision || !state.group.isConnected) return;
      const eligible = resolvePhotos(manifest, state.handle, state.group.dataset.productId,
        button.dataset.variantId, family, state.excluded);
      if (!eligible.length) throw new Error('Verified colour photo unavailable');
      const images = orderedPhotos(eligible, visibleRole(state.card, manifest, state.handle));
      const activeImage = state.card.querySelector('[data-cv4-track] img');
      const photo = await preparePhoto(images[0].url, activeImage?.sizes || '(min-width: 1200px) 25vw, (min-width: 750px) 33vw, 50vw');
      if (revision !== state.revision || !state.group.isConnected) return;
      commitChoice(state, button, images, photo);
    } catch {
      if (revision === state.revision && state.group.isConnected) {
        state.status.textContent = `Fotografia pentru ${button.dataset.metalLabel} nu s-a încărcat. Imaginea și configurația anterioară sunt păstrate. Încearcă din nou.`;
      }
    } finally {
      if (revision === state.revision) button.removeAttribute('aria-busy');
    }
  }
  function mount(group) {
    if (mounted.has(group)) return;
    const card = group.closest('article[data-cs-product-card][data-cs-collection][data-context="collection"]');
    const buttons = [...group.querySelectorAll('button[data-cs-card-metal]')];
    if (!card || !['inele-de-logodna', 'verighete-cu-diamante', 'cercei'].includes(card.dataset.csCollection) ||
        !group.dataset.metalMap || !card.querySelector('[data-cv4-track] img') || buttons.length < 2 ||
        buttons.some(button => !families.has(button.dataset.csCardMetal) || !/^\d+$/.test(button.dataset.variantId))) return;
    const state = { card, group, buttons, revision: 0, demoTouched: card.dataset.cv4Interacted === 'true', handle: group.dataset.productHandle,
      excluded: new Set((group.dataset.metalExcluded || '').split('|').filter(Boolean)),
      status: group.querySelector('[data-cs-card-metal-status]') };
    if (!state.status) return;
    mounted.add(group);
    cardStates.set(card, state);
    watchDemoInteractions(state);
    group.hidden = false;
    card.setAttribute('data-cs-card-metal-enabled', '');
    group.addEventListener('click', event => {
      const button = event.target.closest('button[data-cs-card-metal]');
      if (!button || !buttons.includes(button)) return;
      event.preventDefault();
      event.stopPropagation();
      choose(state, button);
    });
    group.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
      const index = buttons.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 :
        (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next].focus();
    });
  }
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    demonstratedThisVisit.clear();
    const states = [...mobileDemos.values()].map(record => {
      stopMobileDemo(record, 'returned'); return record.state;
    });
    mobileDemos.clear();
    states.forEach(state => scheduleMobileDemo(state));
  });
  function scan() {
    scanFrame = 0;
    for (const record of mobileDemos.values()) {
      if (['waiting', 'loading', 'ready', 'playing'].includes(record.phase) && !mobileDemoCurrent(record)) stopMobileDemo(record, 'invalidated');
      else if (['waiting', 'loading', 'ready'].includes(record.phase)) record.request();
    }
    const groups = [...document.querySelectorAll('[data-cs-card-metals]')];
    groups.forEach(mount);
    groups.forEach(group => {
      const state = cardStates.get(group.closest('article[data-cs-product-card]'));
      if (state) scheduleMobileDemo(state);
    });
  }
  function init() {
    scan();
    new MutationObserver(() => { if (!scanFrame) scanFrame = requestAnimationFrame(scan); })
      .observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
